import {
    Actions,
    Context
} from "vuex-smart-module";

import {
    AxiosError,
    AxiosResponse
} from "axios";

import {
    Store
} from "vuex";

import {
    store
} from "@/store";

import root
    from "@/store/modules";

import esquireClient from "@/modules/esquire-client";

import UtilityCounterState, {UtilityCounterStatus} from "@/store/modules/utility-counter/utility-counter-state";

import UtilityCounterGetters from "@/store/modules/utility-counter/utility-counter-getters";

import UtilityCounterMutations from "@/store/modules/utility-counter/utility-counter-mutations";

import {IUtilityCounterData, UtilityCounterData} from "@/http/data/utility-counter-data";

import UtilityCounterReadingData, {IUtilityCounterReadingData} from "@/http/data/utility-counter-reading-data";

import UtilityCounterBuildingRequest from "@/http/requests/utility-counter-building-request";

import UtilityCounterReadingsRequest from "@/http/requests/utility-counter-readings-request";

import CreateUtilityCounterReadingRequest from "@/http/requests/create-utility-counter-reading-request";

interface UtilityCounterErrorResponse {
    code?: string;
    message?: string;
    errors?: Record<string, string[]>;
}

export interface CreateUtilityCounterReadingPayload {
    request: CreateUtilityCounterReadingRequest;
    onCreated: (reading: UtilityCounterReadingData) => void;
}

function unwrapCollection<T>(payload: unknown, key?: string): T[] {
    if (Array.isArray(payload)) return payload as T[];

    if (!payload || typeof payload !== "object") return [];

    const object = payload as Record<string, unknown>;

    if (key && Array.isArray(object[key])) {
        return object[key] as T[];
    }

    if (Array.isArray(object.data)) {
        return object.data as T[];
    }

    if (Array.isArray(object.items)) {
        return object.items as T[];
    }

    if (Array.isArray(object.result)) {
        return object.result as T[];
    }

    if (object.data && typeof object.data === "object") {
        const data = object.data as Record<string, unknown>;

        if (key && Array.isArray(data[key])) {
            return data[key] as T[];
        }

        if (Array.isArray(data.items)) {
            return data.items as T[];
        }

        if (Array.isArray(data.result)) {
            return data.result as T[];
        }
    }

    return [];
}

function resolveErrorMessage(response: AxiosResponse): string {
    const errorResponse = response.data as UtilityCounterErrorResponse;

    if (errorResponse?.errors) {
        const validationMessage = Object.values(errorResponse.errors)
            .flat()
            .find(message => Boolean(message));

        if (validationMessage) return validationMessage;
    }

    if (errorResponse?.message) return errorResponse.message;

    return `${response.status} ${response.statusText}`;
}

export default class UtilityCounterActions extends Actions<
    UtilityCounterState,
    UtilityCounterGetters,
    UtilityCounterMutations,
    UtilityCounterActions
> {
    private rootContext!: Context<typeof root>;

    $init(_store: Store<typeof store>): void {
        this.rootContext = root.context(_store);

        super.$init(_store);
    }

    async initialize(): Promise<void> {
        this.mutations.setIsInitialized(true);
    }

    async rehydrateStore(): Promise<void> {
        return;
    }

    async initializeForBuilding({buildingId, fromDate, untilDate}: {
        buildingId: string;
        fromDate: string;
        untilDate: string;
    }): Promise<void> {
        this.mutations.setSelectedBuildingId(buildingId);

        this.mutations.setReadingsPeriod({
            fromDate,
            untilDate
        });

        await Promise.all([
            this.actions.indexCounters(buildingId),

            this.actions.indexReadings({
                buildingId,
                fromDate,
                untilDate
            })
        ]);
    }

    async indexCounters(buildingId: string): Promise<void> {
        this.mutations.setCountersStatus(UtilityCounterStatus.Loading);

        const request = new UtilityCounterBuildingRequest(buildingId);

        const response =
            await esquireClient
                .get(
                    "utility-counters/counters",
                    {
                        params:
                            request.toJson()
                    }
                )
                .catch(
                    (error: AxiosError) =>
                        error.response
                );

        if (!response) {
            this.mutations.setCounters([]);

            this.mutations.setCountersStatus(UtilityCounterStatus.Failed);

            await this.actions.notifyMessage("The utility counters could not be loaded.");

            return;
        }

        if (response.status === 200) {
            const payload = unwrapCollection<IUtilityCounterData>(response.data, "counters");
            const counters = payload.map(data => new UtilityCounterData(data));

            this.mutations.setCounters(counters);

            this.mutations.setCountersStatus(
                counters.length > 0
                    ? UtilityCounterStatus.Ready
                    : UtilityCounterStatus.Empty
            );

            return;
        }

        this.mutations.setCounters([]);

        if (response.status === 401) {
            this.mutations.setCountersStatus(UtilityCounterStatus.Unauthenticated);

            return;
        }

        if (response.status === 403) {
            this.mutations.setCountersStatus(UtilityCounterStatus.Forbidden);

            return;
        }

        this.mutations.setCountersStatus(UtilityCounterStatus.Failed);

        await this.notifyUnhandledError(response);
    }

    async indexReadings({buildingId, fromDate, untilDate}: {
        buildingId: string;
        fromDate: string;
        untilDate: string;
    }): Promise<void> {
        this.mutations.setReadingsStatus(UtilityCounterStatus.Loading);

        this.mutations.setReadingsPeriod({fromDate, untilDate});

        const request = new UtilityCounterReadingsRequest(
            buildingId,
            fromDate,
            untilDate
        );

        const response =
            await esquireClient.get("utility-counters/readings",
                {
                    params:
                        request.toJson()
                }
            )
            .catch(
                (error: AxiosError) =>
                    error.response
            );

        if (!response) {
            this.mutations.setReadings([]);

            this.mutations.setReadingsStatus(UtilityCounterStatus.Failed);

            await this.notifyMessage("The counter readings could not be loaded.");

            return;
        }

        if (response.status === 200) {
            const payload = unwrapCollection<IUtilityCounterReadingData>(
                response.data,
                "readings"
            );

            const readings = payload.map(data => new UtilityCounterReadingData(data));

            this.mutations.setReadings(
                readings
            );

            this.mutations.setReadingsStatus(
                readings.length > 0
                    ? UtilityCounterStatus.Ready
                    : UtilityCounterStatus.Empty
            );

            return;
        }

        this.mutations.setReadings([]);

        if (response.status === 401) {
            this.mutations.setReadingsStatus(UtilityCounterStatus.Unauthenticated);

            return;
        }

        if (response.status === 403) {
            this.mutations.setReadingsStatus(UtilityCounterStatus.Forbidden);

            return;
        }

        this.mutations.setReadingsStatus(UtilityCounterStatus.Failed);

        await this.notifyUnhandledError(response);
    }

    async createReading({request, onCreated}: CreateUtilityCounterReadingPayload): Promise<void> {
        const response = await esquireClient
            .post("utility-counters/readings", request.toFormData())
            .catch((error: AxiosError) => error.response);

        if (!response) throw new Error("The counter reading could not be saved.");

        if (response.status !== 200 && response.status !== 201) throw new Error(resolveErrorMessage(response));

        const data = response.data?.data ?? response.data;
        const reading = new UtilityCounterReadingData(
            data as IUtilityCounterReadingData
        );

        this.mutations.upsertReading(reading);
        this.mutations.setReadingsStatus(UtilityCounterStatus.Ready);

        onCreated(reading);
    }

    clear(): void {
        this.mutations.clear();
    }

    private async notifyUnhandledError(response: AxiosResponse): Promise<void> {
        await this.notifyMessage(resolveErrorMessage(response));
    }

    async notifyMessage(message: string): Promise<void> {
        await this.rootContext.actions.message({
            message,
            type: "error",
            duration: 0,
            showClose: true
        });
    }

}